/**
 * StatusIcon — task-status glyph adapter. The visual is now the canonical
 * `StatusRing` (the Linear-style progress ring that fills by workflow state);
 * this thin wrapper keeps the historical name + `TaskStatus` prop so the many
 * list / table / tree / menu call sites don't churn. Prefer `<StatusRing>`
 * directly in new code.
 *
 * Color still routes through TASK_STATUS_INTENT (inside StatusRing), so
 * re-tuning a status color in one file repaints every glyph.
 *
 * STATUS_TEXT_TONE stays here: the text-color class per status, for inline
 * text labels that sit beside the glyph.
 */
import { StatusRing } from "../status-ring";
import { TASK_STATUS_INTENT, type Intent } from "../../tokens";
import { type TaskStatus } from "../../types/projects";

const INTENT_TEXT: Record<Intent, string> = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-destructive",
  neutral: "text-muted-foreground",
};

export const STATUS_TEXT_TONE: Record<TaskStatus, string> = Object.fromEntries(
  (Object.keys(TASK_STATUS_INTENT) as TaskStatus[]).map((s) => [
    s,
    INTENT_TEXT[TASK_STATUS_INTENT[s]],
  ]),
) as Record<TaskStatus, string>;

export function StatusIcon({
  status,
  className,
}: {
  status: TaskStatus;
  className?: string;
}) {
  return <StatusRing status={status} className={className} />;
}
